import { faQuestionCircle as solidQuestionCircle } from "@jewlr/pro-solid-svg-icons/faQuestionCircle"
import { FontAwesomeIcon, MediaBox, Image } from "@jewlr/storybook/core"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { connect } from "react-redux"
import ReactTooltip from "react-tooltip"
import styled from "styled-components"

import {
  updateChoice,
  fetchStylecode,
  trackSlide,
} from "areas/product/store/actions"
import { gemstones } from "helpers/gemstones"
import { to_currency } from "helpers/number"
import { imageLookup } from "helpers/product/image-lookup"
import { useNavigation } from "helpers/use-navigation"

import {
  ChoiceOption,
  GradientContainer,
  OptionsSlider,
  OptionPrice,
  OptionTitle,
} from "./components"

const CategoryContainer = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[10]};
  margin-bottom: 10px;
  :last-child {
    border-bottom: none;
  }
  ${(props) => props.theme.mediaQueries.tablet`
    border-bottom: none;
  `}
`
const OptionDetails = styled(MediaBox)`
  border-bottom: dotted thin ${(props) => props.theme.colors.grey[20]};
  margin-bottom: 10px;
  min-height: ${(props) => (props.hideAttributes ? "50px" : "77px")};
  padding-bottom: 10px;
  padding-left: 0;
`

const OptionDetailsAttribute = styled.p`
  color: ${(props) => (props.isTitle ? props.theme.colors.text : "#757575")};
  font-size: 12px;
  font-weight: normal;
  letter-spacing: 0.5px;
  margin: 0;
`

const CategoryTitle = styled.p`
  color: #313131;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  margin-top: 10px 0;
  padding: ${(props) =>
    props.theme.mediaQueries.tablet ? "0 10px" : "0 15px"};
  text-transform: none;
`

const Tooltip = styled(ReactTooltip)`
  color: ${(props) => props.theme.colors.white};
  margin-left: 10px;
  max-width: 300px;
  text-transform: none;
`
const Stones = ({ category, data, selectedValue, setChoice }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  const hasStoneSelected = category.options.some(
    (opt) => opt.jewlrValue === selectedValue.jewlrValue
  )

  return (
    <OptionsSlider
      aria-label={`${data.heading.title} Options`}
      data-slider="true"
      role="radiogroup"
    >
      {category.options.map((stone, index) => {
        const selected = selectedValue.jewlrValue == stone.jewlrValue
        return (
          <ChoiceOption
            aria-selected={selected}
            data-cy={`product-wizard-option-${data.pref}-${stone.value}`}
            data-is-option="true"
            data-is-selected={selected}
            key={`stoneCategory-${stone.imageKey}`}
            onClick={() => setChoice(data.pref, stone)}
            role="radio"
            selected={selected}
            tabIndex={selected || (index === 0 && !hasStoneSelected) ? 0 : -1}
            {...keyboardProps}
          >
            {stone.use_large_icons ? (
              <Image
                alt={stone.title}
                height="60"
                ratio="60 / 60"
                sizes="60px"
                src={imageLookup("stone", stone.imageKey).x2}
                srcSet={
                  imageLookup("stone", stone.imageKey).x1 +
                  " 1x," +
                  imageLookup("stone", stone.imageKey).x2 +
                  " 2x"
                }
                title={stone.title}
                width="60"
              />
            ) : (
              <Image
                alt={stone.title}
                height="40px"
                ratio="40 / 40"
                sizes="40px"
                src={imageLookup("stone", stone.imageKey).x2}
                srcSet={
                  imageLookup("stone", stone.imageKey).x1 +
                  " 1x," +
                  imageLookup("stone", stone.imageKey).x2 +
                  " 2x"
                }
                title={stone.title}
                width="40px"
              />
            )}
            <OptionTitle display={{ _: "block", tablet: "none" }}>
              {stone.info}
              <br />
              {!data.hide_attributes && stone.subnote && (
                <em>{stone.subnote}</em>
              )}
            </OptionTitle>
            {stone.regular_price ? (
              <OptionPrice>
                <span>
                  <strike>{to_currency(stone.regular_price)}</strike>{" "}
                </span>
                <OptionPrice color="red">
                  {to_currency(stone.price)}
                </OptionPrice>
              </OptionPrice>
            ) : (
              <OptionPrice>
                {stone.price == 0 && data.hide_zero_surcharge
                  ? ""
                  : to_currency(stone.price)}
              </OptionPrice>
            )}
          </ChoiceOption>
        )
      })}
    </OptionsSlider>
  )
}
Stones.propTypes = {
  category: PropTypes.object,
  data: PropTypes.object,
  selectedValue: PropTypes.object,
  setChoice: PropTypes.func,
}

const InternalStoneOptions = ({ data, selectedValue, setChoice }) => {
  if (
    typeof data.heading === "undefined" ||
    data.disabled ||
    (data.options.length <= 1 && !data.override_text)
  ) {
    return null
  }

  const [stoneDetails, setStoneDetails] = useState(selectedValue)
  const [selectedCategory, setSelectedCategory] = useState(null)

  // Keep local state (stoneDetails) sync with the user choice in Redux data (selectedValue)
  useEffect(() => {
    if (
      stoneDetails.value !== selectedValue.value ||
      stoneDetails.price !== selectedValue.price
    ) {
      setStoneDetails(selectedValue)
    }
    const category = data.categories.find((category) =>
      category.options.some(
        (option) => option.title?.trim() === selectedValue.title?.trim()
      )
    )
    if (category !== selectedCategory) {
      setSelectedCategory(category)
    }
  }, [selectedValue])

  const stoneExtraDetails = gemstones.find((gs) =>
    gs.code.includes(stoneDetails.jewlrValue)
  )

  return (
    <div data-horizontal-scroll="true">
      <OptionDetails
        greaterThanOrEqual="tablet"
        hideAttributes={data.hide_attributes}
      >
        <OptionDetailsAttribute isTitle={true}>
          {stoneDetails.stoneName}- {to_currency(stoneDetails.price)}
        </OptionDetailsAttribute>
        {data.stone_size && (
          <OptionDetailsAttribute
            data-cy={`product-wizard-option-stone-size-${data.pref}`}
          >
            Stone Size:{" "}
            <span dangerouslySetInnerHTML={{ __html: data.stone_size }} />
          </OptionDetailsAttribute>
        )}
        {!data.hide_attributes && (
          <div>
            <OptionDetailsAttribute>
              {stoneDetails.birthmonth &&
                "Birthmonth: " + stoneDetails.birthmonth}
            </OptionDetailsAttribute>
            <OptionDetailsAttribute>
              {stoneExtraDetails?.traits && (
                <>
                  <span>Attributes: </span>
                  <span>{stoneExtraDetails.traits.join(", ")}</span>
                </>
              )}
            </OptionDetailsAttribute>
          </div>
        )}
      </OptionDetails>

      {data.categories.map((category, index) => (
        <CategoryContainer key={`category-${category.title}`}>
          {!data.hide_categories && category.title && (
            <CategoryTitle>
              {category.title}{" "}
              <FontAwesomeIcon
                data-for={`wizard-stone__tip_${data.pref}_${index}`}
                data-tip
                fontSize="13px"
                icon={solidQuestionCircle}
              />
            </CategoryTitle>
          )}
          <Tooltip
            id={`wizard-stone__tip_${data.pref}_${index}`}
            multiline
            place="right"
          >
            {category.help_text}
          </Tooltip>
          <GradientContainer>
            <FocusScope>
              <Stones
                category={category}
                data={data}
                selectedValue={selectedValue}
                setChoice={setChoice}
              />
            </FocusScope>
          </GradientContainer>
        </CategoryContainer>
      ))}
    </div>
  )
}

InternalStoneOptions.propTypes = {
  data: PropTypes.object,
  selectedValue: PropTypes.object,
  setChoice: PropTypes.func,
}

const getSelectedValue = (state, ownProps) => {
  if (typeof state.choices[ownProps.pref] === "undefined") {
    return {}
  } else {
    return state.choices[ownProps.pref]
  }
}

const getData = (state, ownProps) => {
  if (typeof state.wizard.data[ownProps.pref] !== "undefined") {
    return state.wizard.data[ownProps.pref]
  } else {
    return { pref: ownProps.pref }
  }
}

const mapStateToProps = (state, ownProps) => {
  const optionData = getData(state, ownProps)
  const selectedValue = getSelectedValue(state, ownProps)
  return {
    data: optionData, // TODO: Use a better name for the prop e.g. optionData
    selectedValue: selectedValue,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    setChoice: (pref, data) => {
      dispatch(updateChoice(pref, data, true))

      // Style code switcher
      if (data.style_code) {
        dispatch(fetchStylecode(data.style_code))
      } else if (data.track_slide) {
        dispatch(trackSlide(data.track_slide))
      }

      try {
        if (typeof ownProps.callback !== "undefined" && ownProps.callback) {
          ownProps.callback()
        }
      } catch (error) {
        console.log("CALLBACK ERROR:", error)
      }
    },
  }
}

const StoneOptions = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalStoneOptions)

export default StoneOptions
